import "./style.scss";

import { RadioFieldProps } from "../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import classNames from "classnames";
import { NodeStates, RadioFieldNode } from "../../build/types";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import RadioOption from "./RadioOption";

const BuilderRadioField = ({ node, builderProps, selectedOption, radioOptions, callback, disabled }: RadioFieldProps) => {
    const rNode = node as RadioFieldNode;
    const { id, attr, stateManager, input } = rNode;
    const { setElement, states } = useInteractionStates<HTMLDivElement>();

    const wrapperClass = classNames("radio-field-wrapper", {
        [`${attr?.className}`]: !!attr?.className,
        ["wrap-radio"]: rNode.wrap
    });

    const theOptions = builderProps ? rNode.previewOptions || radioOptions : radioOptions;

    const inputWrapperStyle = {
        ...rNode.input?.style,
        ...(rNode.input?.style?.justifyContent === "center" ? { justifyContent: "flex-start" } : {})
    };

    const inputWrapperClass = classNames("input-wrapper", {
        ["centered"]: rNode.input?.style?.justifyContent === "center"
    });

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        return (
            <div
                {...attr}
                className={wrapperClass}
                style={getCompoundStyle({
                    node,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                draggable={onDragStart ? "true" : "false"}
                onDragStart={e => onDragStart?.(e, id)}
                onDragEnd={onDragEnd}
            >
                {input?.label && (
                    <p
                        style={getCompoundStyle({
                            otherStyle: input.labelStyle,
                            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "input_labelStyle")
                        })}
                    >
                        {input?.label}
                    </p>
                )}

                <div
                    className={inputWrapperClass}
                    style={getCompoundStyle({
                        otherStyle: inputWrapperStyle,
                        stateStyle: getStateStyleForBuilder(stateManager, nodeState, "inputWrapperStyle")
                    })}
                >
                    {Object.entries(theOptions).map(([value, label], index) => (
                        <RadioOption key={index} node={node} value={value} label={label} builder nodeState={nodeState} />
                    ))}
                </div>
            </div>
        );
    } else {
        const wrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const labelStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: input?.labelStyle,
            stateManager,
            paths: ["input_labelStyle"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const inputWrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: {
                ...rNode.input?.style,
                ...(rNode.input?.style?.justifyContent === "center" ? { justifyContent: "flex-start" } : {})
            },
            stateManager,
            paths: ["input_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <div className={wrapperClass} {...attr} ref={setElement} style={wrapperStyle}>
                {input?.label && <label style={labelStyle}>{input?.label}</label>}

                <div className={inputWrapperClass} style={inputWrapperStyle}>
                    {Object.entries(theOptions).map(([value, label], index) => (
                        <RadioOption
                            key={index}
                            node={node}
                            value={value}
                            label={label}
                            selected={value === selectedOption}
                            onChange={() => callback && callback(value)}
                            disabled={disabled}
                        />
                    ))}
                </div>
            </div>
        );
    }
};

export default BuilderRadioField;
